/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { toast } from 'react-hot-toast';
import { Box, Stack, TextField, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import EditorialSection from '../components/editorial/EditorialSection';
import EditorialButton from '../components/editorial/EditorialButton';

const editorialInputSx = {
  '& .MuiInputBase-root': {
    fontFamily: 'var(--font-body)',
    background: 'transparent',
    borderRadius: 0,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(36,34,88,0.18)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--navy-500)',
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--teal-500) !important',
    borderWidth: '1px !important',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'var(--navy-300)',
  },
  '& .Mui-focused.MuiInputLabel-root': { color: 'var(--teal-500)' },
  '& input, & textarea': {
    color: 'var(--navy-700)',
    fontFamily: 'var(--font-body)',
    fontSize: 16,
  },
};

export default function ContactUs() {
  const [loading, setLoading] = useState(false);
  const [isClicked, setClicked] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', msg: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  useEffect(() => emailjs.init('MqksMNSJT5pJIxqR4'), []);

  const handleSubmit = async () => {
    const serviceId = 'service_b0f5lrc';
    const templateId = 'template_b1izi3p';
    setClicked(true);
    try {
      if (formData.msg && formData.email && formData.firstName && formData.lastName) {
        setLoading(true);
        await emailjs.send(serviceId, templateId, {
          name: `${formData.firstName} ${formData.lastName}`,
          message: formData.msg,
          email: formData.email,
        });
        toast.success('Your message has been sent to KBT!');
        setFormData({ firstName: '', lastName: '', email: '', msg: '' });
        setClicked(false);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <EditorialPageShell title="Contact" description="Write to KBT — Kehilat Bnei Torah, Los Angeles.">
      <PageHero
        kicker="Contact  ·  KBT"
        meta="9017 W. Pico Blvd."
        title="Be in touch."
        titleAccent="Our door is open."
        subtitle="Write to the kehilla. We answer every note — about learning, becoming a member, or stepping inside for the first time."
        image="/assets/images/kbt/siyum.jpg"
        height="74vh"
      />

      <EditorialSection
        kicker="Send a Note"
        title="A line to the kehilla."
        intro="We read every message. Use the form below or write to office@kbtla.org directly."
        watermark="ל"
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
            gap: { xs: 6, md: 10 },
            alignItems: 'start',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <Stack spacing={3.5}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2.5,
                }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={isClicked && !formData.firstName}
                  sx={editorialInputSx}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={isClicked && !formData.lastName}
                  sx={editorialInputSx}
                />
              </Box>
              <TextField
                fullWidth
                variant="outlined"
                label="Email Address"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={isClicked && !formData.email}
                sx={editorialInputSx}
              />
              <TextField
                fullWidth
                variant="outlined"
                label="Your message"
                name="msg"
                value={formData.msg}
                onChange={handleChange}
                error={isClicked && !formData.msg}
                multiline
                minRows={6}
                sx={editorialInputSx}
              />
              <Box>
                <EditorialButton
                  variant="primary"
                  disabled={loading}
                  onClick={handleSubmit}
                >
                  {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Send Message'}
                </EditorialButton>
              </Box>
            </Stack>
          </motion.div>

          {/* Side card: address + schedule */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Box
              sx={{
                background: 'var(--navy-900)',
                color: '#fff',
                p: { xs: 4, md: 5 },
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  right: '-12%',
                  bottom: '-30%',
                  fontFamily: 'var(--font-hebrew)',
                  fontSize: 460,
                  color: '#fff',
                  opacity: 0.04,
                  lineHeight: 0.8,
                  pointerEvents: 'none',
                }}
              >
                ש
              </Box>
              <Stack spacing={4} sx={{ position: 'relative' }}>
                <Box>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10.5,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: 'var(--teal-300)',
                      mb: 1.5,
                    }}
                  >
                    Visit
                  </Box>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: 600,
                      fontSize: 22,
                      lineHeight: 1.3,
                    }}
                  >
                    9017 W. Pico Blvd.<br />
                    Los Angeles, CA 90035
                  </Box>
                </Box>
                <Box>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10.5,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: 'var(--teal-300)',
                      mb: 1.5,
                    }}
                  >
                    Email
                  </Box>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: 500,
                      fontSize: 19,
                      lineHeight: 1.4,
                    }}
                  >
                    office@kbtla.org
                  </Box>
                </Box>
                <Box>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10.5,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: 'var(--teal-300)',
                      mb: 1.5,
                    }}
                  >
                    Daily Rhythm
                  </Box>
                  <Stack spacing={1}>
                    {[
                      ['6:00', 'Amud Yomi'],
                      ['6:45', 'Shacharit'],
                      ['7:30', 'Mishna Berura Yomi'],
                    ].map(([t, l]) => (
                      <Box
                        key={l}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          gap: 3,
                          borderBottom: '1px solid rgba(255,255,255,0.12)',
                          pb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 12,
                            letterSpacing: '0.12em',
                            color: 'var(--teal-100)',
                          }}
                        >
                          {t}
                        </Box>
                        <Box
                          sx={{
                            fontFamily: 'var(--font-display)',
                            fontWeight: 500,
                            fontSize: 17,
                            color: '#fff',
                          }}
                        >
                          {l}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </motion.div>
        </Box>
      </EditorialSection>

      <Box
        component="section"
        sx={{
          position: 'relative',
          background: 'var(--paper-warm)',
          pb: { xs: 10, md: 14 },
        }}
      >
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 3, md: 6 } }}>
          <Box
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--teal-500)',
              mb: 2,
            }}
          >
            Find us
          </Box>
          <Box
            sx={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: { xs: 30, md: 44 },
              lineHeight: 1.05,
              letterSpacing: '-0.018em',
              color: 'var(--navy-500)',
              mb: 5,
              maxWidth: 800,
            }}
          >
            On Pico, between La Cienega and Robertson.
          </Box>
          <Box
            sx={{
              width: '100%',
              height: { xs: 280, md: 460 },
              overflow: 'hidden',
              borderRadius: 0,
              boxShadow: '0 30px 60px -30px rgba(7,6,26,0.35)',
            }}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3305.547774278068!2d-118.38814119999999!3d34.055469699999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2b95e91e7c393%3A0x8f0069aa38b8954f!2s9017%20W%20Pico%20Blvd%2C%20Los%20Angeles%2C%20CA%2090035%2C%20USA!5e0!3m2!1sen!2s!4v1714505823297!5m2!1sen!2s"
              style={{ border: 0, width: '100%', height: '100%', filter: 'grayscale(20%) contrast(1.05)' }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="KBT location"
            />
          </Box>
        </Box>
      </Box>
    </EditorialPageShell>
  );
}
